<!--
  TinoPerformanceChart.vue — Radar chart showing Tino's 6 performance metrics.
  Uses vue-echarts with the app's custom 'sensando' theme.
-->
<script setup>
import { computed, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { RadarChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

use([CanvasRenderer, RadarChart, TooltipComponent, LegendComponent])
provide(THEME_KEY, 'sensando')

const { t } = useI18n()

const metrics = [
  { name: 'Prospectos',  value: 88 },
  { name: 'Conversión',  value: 72 },
  { name: 'Velocidad',   value: 91 },
  { name: 'Autonomía',   value: 94 },
  { name: 'Revenue',     value: 65 },
  { name: 'Retención',   value: 78 },
]

const score = computed(() => {
  const avg = metrics.reduce((s, m) => s + m.value, 0) / metrics.length
  return Math.round(avg)
})

const option = computed(() => ({
  radar: {
    indicator: metrics.map(m => ({ name: m.name, max: 100 })),
    axisName: { fontSize: 11, color: 'rgba(0,0,0,0.5)' },
    splitLine: { lineStyle: { color: 'rgba(99,102,241,0.12)' } },
    splitArea: { show: false },
    axisLine: { lineStyle: { color: 'rgba(99,102,241,0.15)' } },
  },
  series: [{
    type: 'radar',
    data: [{
      value: metrics.map(m => m.value),
      name: 'Tino',
      areaStyle: { color: 'rgba(99,102,241,0.15)' },
      lineStyle: { color: '#6366f1', width: 2 },
      itemStyle: { color: '#6366f1' },
    }],
  }],
  tooltip: { trigger: 'item' },
}))
</script>

<template>
  <VCard class="tino-perf-card" flat>
    <!-- Header -->
    <div class="tino-perf-header">
      <div class="d-flex align-center gap-2">
        <VIcon icon="mdi-radar" size="18" color="primary" />
        <span class="text-sm font-weight-semibold">{{ t('dashboard.tino.performance.title') }}</span>
      </div>
      <VChip size="small" color="primary" variant="flat" class="font-weight-bold">
        {{ t('dashboard.tino.performance.score', { score }) }}
      </VChip>
    </div>

    <VDivider />

    <!-- Radar -->
    <div class="tino-perf-chart-wrap">
      <VChart :option="option" autoresize style="height: 220px; width: 100%;" />
    </div>

    <!-- Metric grid -->
    <div class="tino-metric-grid">
      <div
        v-for="m in metrics"
        :key="m.name"
        class="tino-metric-cell"
      >
        <p class="text-[10px] text-medium-emphasis mb-0">{{ m.name }}</p>
        <p class="text-base font-weight-bold mb-0" style="color: #6366f1">{{ m.value }}</p>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.tino-perf-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
  block-size: 100%;
}

.tino-perf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.tino-perf-chart-wrap {
  padding: 8px 12px 0;
}

.tino-metric-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-block-start: 1px solid rgba(var(--v-border-color), 0.1);
  margin-block-start: 8px;
}

.tino-metric-cell {
  padding: 12px 14px;
  text-align: center;

  &:not(:nth-child(3n)) {
    border-inline-end: 1px solid rgba(var(--v-border-color), 0.1);
  }

  &:nth-child(n+4) {
    border-block-start: 1px solid rgba(var(--v-border-color), 0.1);
  }
}
</style>
